import { injectable } from 'inversify';
import type { FormApi } from 'final-form';

import { START_EVENT_NODE_ID } from '../../constants';
import type { CreateEventTypeDto } from '../../dto';
import type { NodeActionDto } from '../../dto/create-event-type';
import type { FlowNode, FlowSchema } from '../../types/event-types';

const DEFAULT_LABEL = 'Без названия';
const FINISH_LABEL = 'Финиш';
const SYSTEM_LABEL = 'Обработка';
const ACTION_LABELS: Record<string, string> = {
  upload: 'Загрузка',
  generate_document_from_template: 'Загрузка',
  accept: 'Проверка',
  system: 'Обработка',
  decline: 'Отказ',
  sign: 'Подписание',
  return: 'Доработка',
};

type NodeDataDto = CreateEventTypeDto['nodes'][string];

@injectable()
export class EventTypeFlowMapper {
  formToSchema(dto: CreateEventTypeDto): FlowSchema {
    const nodes: FlowNode[] = Object.entries(dto.nodes ?? {}).flatMap(
      ([id, node]) => {
        const schemaNode = this.createSchemaNode(id, node);
        return schemaNode ? [schemaNode] : [];
      },
    );

    return {
      nodes,
      edges: (dto.edges ?? []).map((edge) => ({ ...edge })),
      returns: (dto.returns ?? []).map((edge) => ({ ...edge })),
    };
  }

  schemaToForm(
    schema: FlowSchema,
    base: CreateEventTypeDto,
  ): CreateEventTypeDto {
    const nodes: Record<string, NodeDataDto> = {};

    schema.nodes.forEach((node) => {
      const existNode = base.nodes?.[node.id];

      if (existNode) {
        nodes[node.id] = {
          ...existNode,
          ...(existNode.type === 'UserTask' && {
            parallelGroupId: node.parallelGroupId,
          }),
          ...((existNode.type === 'UserTask' ||
            existNode.type === 'BranchTask') && {
            actions: this.getReturnAction(
              existNode.actions,
              schema.returns,
              node.id,
            ),
          }),
        };
      } else {
        nodes[node.id] = this.createNodeDto(node);
      }
    });

    schema.nodes
      .map((node) => node.parallelGroupId)
      .forEach((groupId) => {
        if (groupId && !nodes[groupId]) {
          nodes[groupId] = base.nodes?.[groupId] ?? {
            type: 'ParallelGroup',
            joinAny: 'false',
          };
        }
      });

    return {
      ...base,
      nodes,
      edges: schema.edges.map((edge) => ({ ...edge })),
      returns: (schema.returns ?? []).map((edge) => ({ ...edge })),
    };
  }

  applySchemaToForm(
    schema: FlowSchema,
    form: FormApi<CreateEventTypeDto>,
  ): void {
    const nextDto = this.schemaToForm(schema, form.getState().values);
    const nodes = this.resetForbiddenFirstNodeActions(nextDto.nodes, schema);

    form.batch(() => {
      form.change('nodes', nodes);
      form.change('edges', nextDto.edges);
      form.change('returns', nextDto.returns);
    });
  }

  withIsFirstNode(
    node: NodeDataDto,
    nodeId: string,
    edges: FlowSchema['edges'],
  ): NodeDataDto {
    if (node.type !== 'UserTask' && node.type !== 'BranchTask') return node;

    const isFirstNode = edges.some(
      (edge) =>
        edge.sourceId === START_EVENT_NODE_ID && edge.targetId === nodeId,
    );

    return { ...node, isFirstNode };
  }

  private resetForbiddenFirstNodeActions(
    nodes: CreateEventTypeDto['nodes'],
    schema: FlowSchema,
  ): CreateEventTypeDto['nodes'] {
    const result = { ...nodes };

    schema.edges
      .filter((edge) => edge.sourceId === START_EVENT_NODE_ID)
      .forEach(({ targetId }) => {
        const node = result[targetId];
        if (node?.type !== 'UserTask') return;

        result[targetId] = {
          ...node,
          actions: node.actions.map((action) =>
            action?.isPrimary && action.type === 'sign'
              ? { ...action, type: undefined, signType: undefined }
              : action,
          ),
        };
      });

    return result;
  }

  processIncompleteBranches(schema: FlowSchema): boolean {
    const sourceIds = new Set(schema.edges.map((edge) => edge.sourceId));

    return schema.nodes.some(
      (node) => !sourceIds.has(node.id) && node.type !== 'FinishEvent',
    );
  }

  private createSchemaNode(id: string, node: NodeDataDto): FlowNode | null {
    const stageName = 'stageName' in node ? node.stageName : '';
    const actions = 'actions' in node ? node.actions : undefined;

    switch (node.type) {
      case 'FinishEvent':
        return {
          id,
          type: 'FinishEvent',
          label: FINISH_LABEL,
          actionType: actions?.[0]?.type,
        };
      case 'StartEvent':
        return { id, type: 'StartEvent', label: stageName || DEFAULT_LABEL };
      case 'SystemTask':
        return {
          id,
          type: 'SystemTask',
          label: SYSTEM_LABEL,
        };
      case 'UserTask':
      case 'BranchTask': {
        const primary = this.getPrimaryAction(actions);
        return {
          id,
          type: node.type,
          label: this.getActionLabel(primary),
          actionType: primary?.type,
          allowReturnTo: true,
          parallelGroupId: node.parallelGroupId,
          hasDeadline: this.getHasDeadline(node),
        };
      }
      default:
        return null;
    }
  }

  private getHasDeadline(node: { hasDeadline?: string }): boolean {
    return !!node.hasDeadline && JSON.parse(node.hasDeadline);
  }

  private getReturnAction(
    actions: NodeActionDto[],
    returns: FlowSchema['returns'],
    nodeId: string,
  ): NodeActionDto[] {
    const hasReturn = (returns ?? []).some((edge) => edge.sourceId === nodeId);
    const returnAction = actions.find((action) => action.type === 'return');

    return hasReturn
      ? [
          ...actions.filter((action) => action.type !== 'return'),
          returnAction ?? { type: 'return', isPrimary: false },
        ]
      : actions.filter((action) => action.type !== 'return');
  }

  private getPrimaryAction(
    actions: NodeActionDto[] | undefined,
  ): NodeActionDto | undefined {
    if (!actions?.length) return undefined;
    return actions.find((action) => action.isPrimary) || actions[0];
  }

  private getActionLabel(action: NodeActionDto | undefined): string {
    if (!action?.type) return DEFAULT_LABEL;
    return ACTION_LABELS[action.type];
  }

  private createNodeDto(node: FlowNode): NodeDataDto {
    switch (node.type) {
      case 'StartEvent':
        return {
          type: 'StartEvent',
          stageName: '',
          createForOthers: false,
          batchEnabled: false,
          noEmployee: false,
          executor: [],
          notifyIntegration: false,
        };
      case 'FinishEvent':
        return {
          type: 'FinishEvent',
          actions: [{ type: node.actionType ?? 'accept', isPrimary: true }],
          notifyIntegration: false,
        };
      case 'SystemTask':
        return {
          type: 'SystemTask',
          stageName: '',
        };
      case 'BranchTask':
        return {
          type: 'BranchTask',
          stageName: '',
          executor: [],
          notifyIntegration: false,
          companyCancelable: true,
          employeeCancelable: false,
          hasDeadline: 'false',
          actions: [
            { type: 'accept', isPrimary: true },
            { type: 'decline', isPrimary: false },
          ],
          parallelGroupId: node.parallelGroupId,
        };
      case 'UserTask':
      default:
        return {
          type: 'UserTask',
          stageName: '',
          executor: [],
          notifyIntegration: false,
          companyCancelable: true,
          employeeCancelable: false,
          hasDeadline: 'false',
          actions: [{ isPrimary: true }],
          parallelGroupId: node.parallelGroupId,
        };
    }
  }
}
